import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { useIntl } from "gatsby-plugin-react-intl"
import { useMedia } from "react-use"

import CopyShare from "./CopyShare"

import { media, mixins } from "../styles"
import { isMobileDevice } from "../helpers"

import FacebookSVG from "../images/icons/facebook-outline.inline.svg"
import LinkedinSVG from "../images/icons/linkedin-outline.inline.svg"
import LinkSVG from "../images/icons/link.inline.svg"
import ShareSVG from "../images/icons/share.inline.svg"

const SocialShareWidget = styled.div``

const SocialShareLabel = styled.div`
  ${mixins.copy5};
  color: ${({ theme }) => theme.sage};
  display: block;
  margin-bottom: 10px;
  text-align: center;

  @media ${media.lg} {
    display: none;
  }
`
const SocialShareList = styled.ul`
  display: flex;
  flex-flow: row nowrap;
  list-style: none;
  margin: 0 auto;
  justify-content: center;
  padding: 0;

  @media ${media.lg} {
    flex-direction: column;
  }
`

const SocialShareItem = styled.li`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0 10px;

  @media ${media.lg} {
    margin: 0 auto 15px;
  }

  a {
    color: inherit;
  }
`

const SocialShareIcon = styled.span`
  align-items: center;
  border-radius: 100%;
  border: 1px solid ${({ theme }) => theme.sage};
  color: ${({ theme }) => theme.sage};
  display: flex;
  height: 38px;
  justify-content: center;
  opacity: 0.5;
  overflow: hidden;
  transition: opacity ${({ theme }) => theme.transitionTime};
  width: 38px;

  &:hover {
    opacity: 1;
  }

  svg {
    display: block;
    transform: translate3d(0, 0, 0); /* Safari weirdness */
  }
`

const SocialShareNativeWidget = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;

  svg {
    color: ${({ theme }) => theme.sun};
    margin-left: 10px;
  }
`

const SocialShareNativeButton = styled.button`
  align-items: self-end;
  appearance: none;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.sage};
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  margin: 0;
  outline: none;
  padding: 0;
`

const SocialShareNative = ({ label, text, url }) => {
  const nativeShare = () => {
    navigator
      .share({ text, url })
      .then(() => console.log("Successful share"))
      .catch((error) => console.log("Error sharing", error))
  }
  return (
    <SocialShareNativeWidget>
      <SocialShareNativeButton onClick={() => nativeShare()}>
        {label}
        <ShareSVG />
      </SocialShareNativeButton>
    </SocialShareNativeWidget>
  )
}

const SocialShare = ({ label, text, url }) => {
  const intl = useIntl()
  const isMd = useMedia(media.md, false)
  const [isNativeShareSupported, setIsNativeShareSupported] = useState(false)

  useEffect(() => {
    if (!isMd && isMobileDevice() && navigator.share) {
      setIsNativeShareSupported(true)
    }
  }, [isMd])

  return (
    <SocialShareWidget>
      {isNativeShareSupported ? (
        <SocialShareNative
          label={label || intl.formatMessage({ id: "socialShare.shareThis" })}
          text={text}
          url={url}
        />
      ) : (
        <>
          <SocialShareLabel>
            {label || intl.formatMessage({ id: "socialShare.shareThis" })}
          </SocialShareLabel>
          <SocialShareList>
            <SocialShareItem>
              <a
                aria-label={intl.formatMessage(
                  { id: "socialShare.shareOn" },
                  { platform: "Facebook" }
                )}
                href={`http://www.facebook.com/sharer/sharer.php?u=${url}`}
                rel="noopener noreferrer nofollow"
                target="_blank"
                title={intl.formatMessage(
                  { id: "socialShare.shareOn" },
                  { platform: "Facebook" }
                )}
              >
                <SocialShareIcon>
                  <FacebookSVG />
                </SocialShareIcon>
              </a>
            </SocialShareItem>
            <SocialShareItem>
              <a
                aria-label={intl.formatMessage(
                  { id: "socialShare.shareOn" },
                  { platform: "LinkedIn" }
                )}
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${encodeURIComponent(
                  text
                )}`}
                rel="noopener noreferrer nofollow"
                target="_blank"
                title={intl.formatMessage(
                  { id: "socialShare.shareOn" },
                  { platform: "LinkedIn" }
                )}
              >
                <SocialShareIcon>
                  <LinkedinSVG />
                </SocialShareIcon>
              </a>
            </SocialShareItem>
            <SocialShareItem>
              <CopyShare copy={`${text} ${url}`} showTooltip>
                <SocialShareIcon>
                  <LinkSVG />
                </SocialShareIcon>
              </CopyShare>
            </SocialShareItem>
          </SocialShareList>
        </>
      )}
    </SocialShareWidget>
  )
}

SocialShare.propTypes = {
  label: PropTypes.string,
  text: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
}

export default SocialShare
